import React, { useState} from 'react';
import { Code, Eye } from 'lucide-react';
import ReactQuill from 'react-quill';
import hljs from 'highlight.js';
import ImageResize from 'quill-image-resize-module-react';
import 'react-quill/dist/quill.snow.css';
import 'highlight.js/styles/github.css';
import Quill from 'quill';

Quill.register('modules/imageResize', ImageResize);

// Access API key from Vite environment
const uploadApiKey = process.env.REACT_APP_IMAGE_UPLOAD_API_KEY;

const handleImageUpload = async (file) => {
  const formData = new FormData();
  formData.append('image', file);

  const res = await fetch(`https://api.imgbb.com/1/upload?key=${uploadApiKey}`, {
    method: 'POST',
    body: formData,
  });

  const data = await res.json();
  return data.data.url;
};

const imageHandler = function () {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = async () => {
    const file = input.files[0];
    const range = this.quill.getSelection();

    try {
      const imageUrl = await handleImageUpload(file);
      this.quill.insertEmbed(range.index, 'image', imageUrl);
    } catch (err) {
      console.error('Image upload failed:', err);
    }
  };
};

const quillModules = {
  syntax: {
    highlight: text => hljs.highlightAuto(text).value,
  },
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    handlers: {
      image: imageHandler,
    },
  },
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize'],
  },
};

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'blockquote', 'code-block',
  'link', 'image', 'video'
];

const BlogEditor = ({ content, onChange }) => {
  const [editorMode, setEditorMode] = useState('wysiwyg'); // 'wysiwyg' or 'html'

  const handleContentChange = (value) => {
    onChange(value);
  };

  const handleTextareaChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-2">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          Content
        </label>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setEditorMode('wysiwyg')}
            className={`flex items-center px-3 py-1 rounded-md text-xs font-medium ${
              editorMode === 'wysiwyg'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
            }`}
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            WYSIWYG
          </button>
          <button
            type="button"
            onClick={() => setEditorMode('html')}
            className={`flex items-center px-3 py-1 rounded-md text-xs font-medium ${
              editorMode === 'html'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
            }`}
          >
            <Code className="h-3.5 w-3.5 mr-1" />
            HTML
          </button>
        </div>
      </div>

      {editorMode === 'wysiwyg' ? (
        <ReactQuill
          theme="snow"
          modules={quillModules}
          formats={quillFormats}
          value={content}
          onChange={handleContentChange}
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
        />
      ) : (
        <div className="relative">
          <textarea
            value={content}
            onChange={handleTextareaChange}
            className="w-full h-80 px-4 py-3 font-mono text-sm bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
            placeholder="<p>Write your HTML content here...</p>"
          />
          <div className="absolute top-2 right-2 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs px-2 py-1 rounded">
            HTML Mode
          </div>
        </div>
      )}

      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {editorMode === 'html'
          ? 'You can write raw HTML to have complete control over formatting.'
          : 'Use the toolbar to format your content or switch to HTML mode for direct HTML editing.'}
      </p>
    </div>
  );
};

export default BlogEditor;
